import { Link } from "@tanstack/react-router";
import { Instagram, Facebook, Youtube, Mail, Phone, MapPin } from "lucide-react";

import { useSiteSettings } from "@/lib/use-site-settings";

const FOOTER_NAV = [
  { to: "/quem-e", label: "Quem é Rurdiney" },
  { to: "/propostas", label: "Propostas" },
  { to: "/trabalhos", label: "Trabalhos" },
  { to: "/noticias", label: "Notícias" },
  { to: "/agenda", label: "Agenda" },
  { to: "/videos", label: "Vídeos" },
  { to: "/galeria", label: "Galeria" },
  { to: "/materiais", label: "Materiais" },
  { to: "/contato", label: "Contato" },
  { to: "/privacidade", label: "Privacidade" },
] as const;

export function SiteFooter() {
  const s = useSiteSettings();

  return (
    <footer className="bg-primary-deep text-primary-foreground/70">
      <div className="mx-auto max-w-7xl px-4 py-14">
        <div className="grid gap-10 md:grid-cols-4">
          {/* Brand */}
          <div className="md:col-span-2">
            <div className="flex items-center gap-3">
              {s.logoLightUrl || s.logoUrl ? (
                <img
                  src={s.logoLightUrl || s.logoUrl}
                  alt={`Logo ${s.candidateName} — Nº ${s.candidateNumber}`}
                  className="h-14 w-auto max-w-[240px] object-contain"
                />
              ) : (
                <div className="leading-tight">
                  <span className="block font-display text-xl text-primary-foreground">
                    {s.candidateName}
                  </span>
                  <span className="block text-sm font-bold uppercase tracking-widest text-primary-foreground">
                    Nº {s.candidateNumber}
                  </span>
                </div>
              )}
            </div>
            <p className="mt-4 max-w-md text-sm leading-relaxed">
              {s.slogan}
            </p>
            <div className="mt-5 flex gap-3">
              {s.instagram && (
                <a
                  href={s.instagram}
                  target="_blank"
                  rel="noreferrer"
                  className="flex size-9 items-center justify-center rounded-lg bg-white/10 transition-colors hover:bg-accent hover:text-primary-foreground-foreground"
                  aria-label="Instagram"
                >
                  <Instagram className="size-4" />
                </a>
              )}
              {s.facebook && (
                <a
                  href={s.facebook}
                  target="_blank"
                  rel="noreferrer"
                  className="flex size-9 items-center justify-center rounded-lg bg-white/10 transition-colors hover:bg-accent hover:text-primary-foreground-foreground"
                  aria-label="Facebook"
                >
                  <Facebook className="size-4" />
                </a>
              )}
              {s.youtube && (
                <a
                  href={s.youtube}
                  target="_blank"
                  rel="noreferrer"
                  className="flex size-9 items-center justify-center rounded-lg bg-white/10 transition-colors hover:bg-accent hover:text-primary-foreground-foreground"
                  aria-label="YouTube"
                >
                  <Youtube className="size-4" />
                </a>
              )}
            </div>
          </div>

          {/* Nav */}
          <div>
            <h3 className="font-display text-sm uppercase tracking-widest text-primary-foreground">
              Navegação
            </h3>
            <ul className="mt-4 grid gap-2">
              {FOOTER_NAV.map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    className="text-sm transition-colors hover:text-primary-foreground"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-display text-sm uppercase tracking-widest text-primary-foreground">
              Contato
            </h3>
            <ul className="mt-4 grid gap-3 text-sm">
              {s.email && (
                <li className="flex items-center gap-2">
                  <Mail className="size-4 shrink-0 text-accent" />
                  <a href={`mailto:${s.email}`} className="hover:text-primary-foreground">
                    {s.email}
                  </a>
                </li>
              )}
              {s.phone && (
                <li className="flex items-center gap-2">
                  <Phone className="size-4 shrink-0 text-accent" />
                  <span>{s.phone}</span>
                </li>
              )}
              {s.address && (
                <li className="flex items-center gap-2">
                  <MapPin className="size-4 shrink-0 text-accent" />
                  <span>{s.address}</span>
                </li>
              )}
            </ul>
          </div>
        </div>

        <div className="mt-12 border-t border-white/10 pt-6">
          <p className="text-xs leading-relaxed text-primary-foreground/50">
            {s.footerText}
            {s.cnpj && ` • CNPJ: ${s.cnpj}`}
            <br />
            Conteúdo eleitoral. Autorizado por {s.candidateName} — CNP {s.cnpj}.
          </p>
        </div>
      </div>
    </footer>
  );
}
